(ns ui.view.entity-creator.investor.event
  (:require
   [common.entity :as entity]
   [common.localization :as loc]
   [common.schema.registry :as registry]
   [common.util :as cu]
   [frontend.controller.websocket.event :as ws]
   [frontend.logging :as logging]
   [frontend.state.current-session :as session]
   [frontend.view.modals.events :as modals]
   [potpuri.core :as potpuri]
   [re-frame.core :as rf]
   [ui.cofx.util :as cofx]
   [ui.fx.toast :as toast]
   [ui.view.entity-creator.investor.model :as m]
   [ui.view.entity-creator.model :as creator-m]
   [ui.view.entity-creator.utils :as u]))

(defn initialize
  [{:keys [db]}
   [_ {:keys [ident] :as _args}
    {:keys [on-ok! on-processed! close-modal!] :as _callbacks}]]
  (let [entity-type :entity.type/investor
        new-db (-> db
                   (assoc-in (m/entity-type-full-path ident) entity-type)
                   (assoc-in (m/callbacks-full-path ident)
                             {:on-ok! on-ok!
                              :on-processed! on-processed!
                              :close-modal! close-modal!}))]
    {:db new-db}))

(rf/reg-event-fx
 m/initialize
 []
 #'initialize)

(defn close-creator
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/i-root ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/close-creator
 []
 #'close-creator)

(defn confirm-legal-entity
  [{:keys [db legal-entity]}
   [_ {:keys [ident legal-entity-id] :as args}]]
  (let [current-legal-entity (get-in db (m/selected-legal-entity-full-path ident))
        ;; click create new, click back, click next:
        continuing-new? (and (some? legal-entity-id)
                             (= legal-entity-id (:db/id current-legal-entity)))
        set-legal-entity? (and (or (contains? args :legal-entity-id)
                                   (contains? args :legal-entity-type))
                               (not continuing-new?))
        new-db (cond-> db
                 true (assoc-in (m/current-view-full-path ident) :entity-creator)
                 true (potpuri/dissoc-in (m/initial-legal-entity-value-full-path ident))
                 set-legal-entity? (assoc-in (m/selected-legal-entity-full-path ident)
                                             legal-entity))]
    {:db new-db}))

(rf/reg-event-fx
 m/confirm-legal-entity
 [(rf/inject-cofx m/selected-legal-entity-cofx)]
 #'confirm-legal-entity)

(defn unselect-legal-entity
  [{:keys [db]} [_ {:keys [ident] :as _args}]]
  {:db (-> db
           (potpuri/dissoc-in (m/selected-legal-entity-full-path ident)))})

(rf/reg-event-fx
 m/unselect-legal-entity
 []
 #'unselect-legal-entity)

(defn selected-legal-entity-cofx
  [{:keys [original-event] :as cofx}]
  (let [[_ {:keys [legal-entity-id legal-entity-data legal-entity-type
                   initial-value]}] original-event
        existing-legal-entity? (number? legal-entity-id)
        person? (= legal-entity-type :entity.type/person)
        initial-attributes (when (and (not existing-legal-entity?)
                                      (keyword? legal-entity-type)
                                      (string? initial-value)
                                      (not (cu/blank-value? initial-value)))
                             (if person?
                               (u/try-parse-contact-info initial-value)
                               {(registry/resolve-name-attribute legal-entity-type)
                                (cu/str-trim initial-value)}))
        legal-entity (cond existing-legal-entity?
                           ;; The caller passes the entity that it loaded from
                           ;; the backend (`entity-scope/load-entity!`). There
                           ;; is no session lookup here: the session storage is
                           ;; going away, and a session miss dropped the choice
                           ;; of the user without a message.
                           (or legal-entity-data
                               (logging/error
                                "selected-legal-entity-cofx: an existing legal entity needs :legal-entity-data, id"
                                legal-entity-id))

                           (keyword? legal-entity-type)
                           (cond-> {:db/id (str "new-" (name legal-entity-type))
                                    :entity/type legal-entity-type}
                             (and (string? initial-value)
                                  (not (cu/blank-value? initial-value)))
                             (merge initial-attributes)))]
    (cond-> cofx
      legal-entity (assoc :legal-entity legal-entity))))

(rf/reg-cofx
 m/selected-legal-entity-cofx
 #'selected-legal-entity-cofx)

(defn legal-entity-load-failed
  "Tells the user that the selected legal entity could not be loaded. The
   creator stays on the picker, so the user can select again."
  [_ _]
  {:fx [[::toast/error (loc/tr :creator/selection-load-failed)]]})

(rf/reg-event-fx
 m/legal-entity-load-failed
 []
 #'legal-entity-load-failed)

(defn existing-investor-cofx
  [{:keys [db original-event] :as cofx}]
  (let [[_ {:keys [ident]}] original-event
        legal-entity (-> db
                         (get-in (m/selected-legal-entity-full-path ident)))
        legal-entity-id (:db/id legal-entity)
        existing-legal-entity? (number? legal-entity-id)
        investor (when existing-legal-entity?
                   (->> @session/investors
                        (cu/find-first-by :investor/legal-entity legal-entity-id)))]
    (cond-> cofx
      investor (assoc :existing-investor investor))))

(rf/reg-cofx
 m/existing-investor-cofx
 #'existing-investor-cofx)

(defn navigate-back
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (-> db
                   (assoc-in (m/current-view-full-path ident) :select-legal-entity)
                   (potpuri/dissoc-in (m/initial-legal-entity-value-full-path ident)))]
    {:db new-db}))

(rf/reg-event-fx
 m/navigate-back
 []
 #'navigate-back)

(defn navigate-next
  [{:keys [db]}
   [_ {:keys [ident] :as _args}]]
  (let [new-db (-> db
                   (assoc-in (m/current-view-full-path ident) :entity-creator))]
    {:db new-db}))

(rf/reg-event-fx
 m/navigate-next
 []
 #'navigate-next)

(defn start-create
  [{:keys [db tenant-schema default-currency now existing-investor]}
   [_ {:keys [ident] :as args}]]
  (let [entity-type (-> db
                        (get-in (m/entity-type-full-path ident)))
        entity-edits (-> db
                         (get-in (creator-m/entity-edits-full-path
                                  (m/resolve-investor-creator-ident args))))
        entity-schema (registry/get-schema tenant-schema entity-type)
        investor (u/normalize-entity-edits
                  {:entity-schema entity-schema
                   :default-currency default-currency}
                  (assoc entity-edits :entity/type entity-type))
        legal-entity (-> db
                         (get-in (m/selected-legal-entity-full-path ident)))
        legal-entity-id (:db/id legal-entity)
        legal-entity-type (:entity/type legal-entity)
        ;; no editing existing entity, only for newly created:
        legal-entity-edits? (string? legal-entity-id)

        legal-entity-schema (when legal-entity-type
                              (registry/get-schema tenant-schema legal-entity-type))
        legal-entity-edits (when legal-entity-edits?
                             (u/normalize-entity-edits
                              {:entity-schema legal-entity-schema
                               :default-currency default-currency}
                              (-> db
                                  (get-in (creator-m/entity-edits-full-path
                                           (m/resolve-legal-entity-creator-ident args)))
                                  (assoc :db/id legal-entity-id
                                         :entity/type legal-entity-type))))
        legal-entity-name (entity/display-string (or legal-entity-edits
                                                     legal-entity) :long)
        investor-name (:investor/name investor)
        update-name? (and (not existing-investor)
                          (not= legal-entity-name investor-name))
        investor-edits (cond-> investor
                         existing-investor (assoc :db/id (:db/id existing-investor))
                         (not existing-investor) (assoc :db/id "new-investor")
                         update-name? (assoc :investor/name legal-entity-name)
                         legal-entity-edits? (assoc :investor/legal-entity legal-entity-id))
        data {:investor investor-edits}
        data (cond-> data
               (and (not (cu/empty-value? legal-entity-edits))
                    legal-entity-edits?) (assoc :legal-entity
                                                legal-entity-edits))
        new-db (-> db
                   (assoc-in (m/processing-full-path ident) true)
                   (assoc-in (m/processing-start-time-full-path ident) now))
        new-db (cond-> new-db
                 (not existing-investor) (assoc-in (m/creating-full-path ident) true))]
    {:db new-db
     ::ws/send
     {:id :entity/upsert-investor
      :body data
      :on-success [m/create-success args]
      :on-error [m/create-error args]}}))

(rf/reg-event-fx
 m/start-create
 [(rf/inject-cofx cofx/tenant-schema-cofx)
  (rf/inject-cofx cofx/default-currency-cofx)
  (rf/inject-cofx cofx/now-cofx)
  (rf/inject-cofx m/existing-investor-cofx)]
 #'start-create)

(defn create-error
  [{:keys [db]} [_ {:keys [ident] :as _args} error-response]]
  (let [{:keys [error message]} error-response
        msg (cond (string? message) message
                  (keyword? error) (loc/tr error)
                  :else (loc/tr :unknown-error-persists))
        new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident))
                   (potpuri/dissoc-in (m/creating-full-path ident)))]
    {:db new-db
     :fx [[::modals/open-modal (partial u/open-error-modal! msg)]]}))

(rf/reg-event-fx
 m/create-error
 []
 #'create-error)

(defn create-success
  [{:keys [db now]} [_ {:keys [ident] :as args} success-response]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        processing-start-time (get-in db (m/processing-start-time-full-path ident))
        {:keys [on-processed!]} callbacks
        new-entity (:body success-response)
        keep-processing? (fn? on-processed!)

        ;; m/processing-done immediately if no on-processed!,
        ;; else on-processed! :close-cb should be called with dispatch m/processing-done
        after-processed! (fn [_]
                           (js/setTimeout
                            (fn []
                              (rf/dispatch [m/processing-done args]))
                            (u/resolve-processing-time processing-start-time now)))
        callback (when keep-processing?
                   (partial on-processed!
                            {:new-entity-id (:db/id new-entity)
                             :new-entity    new-entity
                             :close-cb!     after-processed!}))
        new-db (-> db
                   (assoc-in (m/created-entity-full-path ident) new-entity))]
    {:db new-db
     :fx [(if-not keep-processing?
            [:dispatch-later
             {:ms (u/resolve-processing-time processing-start-time now)
              :dispatch [m/processing-done args]}]
            [m/callback-fx callback])]}))

(rf/reg-event-fx
 m/create-success
 [(rf/inject-cofx cofx/now-cofx)]
 #'create-success)

(defn processing-done
  [{:keys [db]} [_ {:keys [ident skip-success-msg?] :as args}]]
  (let [new-db (-> db
                   (potpuri/dissoc-in (m/processing-full-path ident))
                   (potpuri/dissoc-in (m/creating-full-path ident))
                   (assoc-in (m/current-view-full-path ident)
                             :done))]
    {:db new-db
     :fx [(when skip-success-msg?
            [:dispatch [m/finish args]])]}))

(rf/reg-event-fx
 m/processing-done
 []
 #'processing-done)

(defn finish
  [{:keys [db]} [_ {:keys [ident] :as args}]]
  (let [callbacks (get-in db (m/callbacks-full-path ident))
        {:keys [on-ok! close-modal!]} callbacks
        new-entity (-> db
                       (get-in (m/created-entity-full-path ident)))
        ok! (if (fn? on-ok!)
              (partial on-ok! {:new-entity-id (:db/id new-entity)
                               :new-entity    new-entity
                               :close-cb!     close-modal!})
              close-modal!)]
    {:fx [[:dispatch [m/close-creator args]]
          [m/callback-fx ok!]]}))

(rf/reg-event-fx
 m/finish
 []
 #'finish)

(rf/reg-fx
 m/callback-fx
 (fn [callback]
   ;; rf/dispatch-sync not allowed in rf-event-handlers
   ;; => setTimeout as workaround:
   (js/setTimeout callback 0)))
